"use client";

/**
 * 🔴 2026-10-14（第八轮）：分镜长文案的**结构化呈现**。
 *
 * 用户原话：「看着信息太乱了帮我整理一下数据呈现的呈现格式，要便于阅读」。
 *
 * ## 为什么要拆成独立组件
 *
 * 解析逻辑在纯模块 `shot-text-blocks.ts`（可离线测、0 token）；
 * 这里只负责**把块渲染成可读的版式**，两者职责分开：
 * 判据改动不必碰 UI，UI 改版式不必重跑解析。
 *
 * ## 版式约定（按块的 kind）
 *
 * | kind       | 呈现                                     |
 * |------------|------------------------------------------|
 * | `meta`     | 一行灰底标签：`Duration` + 值            |
 * | `timeline` | 左侧时间码胶囊 + 正文（**主体版式**）    |
 * | `state`    | 左侧状态标签（起始/结束）+ 正文          |
 * | `reference`| 左侧 `@图片N` 标签 + 名称 + 正文         |
 * | `prose`    | 普通段落（无标记时才走这里）             |
 *
 * 🔴 **不重写、不翻译、不删改任何字**：只有标签（时间码/图片号）是抽出来的，
 * 正文原样显示。切不出结构的文案仍走 prose 分支（宁可不给样式，也不给错样式）。
 */

import { useMemo } from "react";
import {
  parseShotText,
  splitInlineRefs,
  type TextBlock,
} from "@/lib/video/shot-text-blocks";
import { cn } from "@/lib/utils";

/**
 * 正文渲染：`@图片N（名称）` 内联高亮。
 *
 * 🔴 引用常常夹在句子中间（真库镜 1：`在 @图片2（破败浴室镜前）昏暗潮湿的光线中，@图片1（林汐）双手…`），
 * 不能切块（会把一句话劈碎），只能原位高亮 —— 否则用户数不清引用了几张、分别是谁。
 */
function RichText({
  text,
  className,
  ...rest
}: {
  text: string;
  className?: string;
  [k: string]: unknown;
}) {
  const parts = useMemo(() => splitInlineRefs(text), [text]);
  return (
    <p className={cn("whitespace-pre-wrap text-xs leading-6 text-white/85", className)} {...rest}>
      {parts.map((p, i) =>
        typeof p === "string" ? (
          <span key={i}>{p}</span>
        ) : (
          <span
            key={i}
            data-inline-ref={p.label}
            title={p.name ? `参考图 ${p.label}：${p.name}` : `参考图 ${p.label}`}
            className="mx-px inline-flex items-center gap-0.5 rounded bg-emerald-500/20 px-1 align-baseline text-[10px] font-medium leading-4 text-emerald-200"
          >
            {p.label}
            {p.name ? <span className="max-w-[9rem] truncate text-emerald-300/80">{p.name}</span> : null}
          </span>
        )
      )}
    </p>
  );
}

const KIND_STYLE: Record<
  Exclude<TextBlock["kind"], "prose">,
  { chip: string; label: string }
> = {
  meta: { chip: "bg-white/10 text-white/60", label: "font-sans text-[10px] tracking-wide" },
  timeline: { chip: "bg-primary/25 text-[#ffb4a2] font-mono tabular-nums", label: "font-mono text-[10px]" },
  state: { chip: "bg-sky-500/20 text-sky-200", label: "font-sans text-[10px]" },
  reference: { chip: "bg-emerald-500/20 text-emerald-200", label: "font-mono text-[10px]" },
};

export function ShotTextBlocks({
  text,
  empty,
  className,
  wrapperProps,
}: {
  text: string | null | undefined;
  empty: string;
  className?: string;
  /** 🔴 挂到最外层容器的额外属性（如真机脚本定位用的 data-* 标识） */
  wrapperProps?: Record<string, string>;
}) {
  const blocks = useMemo(() => parseShotText(text), [text]);

  if (blocks.length === 0) {
    return (
      <div className={className} {...(wrapperProps ?? {})}>
        <p className="text-xs text-white/40">{empty}</p>
      </div>
    );
  }

  return (
    <div className={cn("space-y-2", className)} {...(wrapperProps ?? {})}>
      {blocks.map((b, i) => {
        if (b.kind === "prose") {
          return <RichText key={i} text={b.text} data-block-kind="prose" />;
        }
        const st = KIND_STYLE[b.kind];
        const label = (b as { label: string }).label;
        /**
         * 🔴 2026-10-16（移动端反馈「排版没有对齐」）：**标签列必须真的定宽**。
         *
         * 旧实现只有 `shrink-0` —— 它只**防收缩**，不锁宽度，标签宽仍随文本变：
         * `0-2s`（4 字符）与 `100-120s`（7 字符）宽度差一截 ⇒ 每行正文起点不同
         * ⇒ 时间轴看起来参差不齐。注释写着「定宽」、门禁也断言 `shrink-0` 就算「定宽」
         * —— 但**行为从来没定宽过**（假绿门禁）。
         *
         * 现在：
         *   · `timeline / meta / state` ⇒ **`w-16` 固定列**：
         *     实测最长标签 `100-120s` 在 10px 等宽（≈6px/字符）下 ≈48px，
         *     加左右 `px-1.5`(12px) = 60px —— 所以 `w-14`(56px) 会溢出，必须 `w-16`(64px，内容区 52px)。
         *     另配 `truncate` 作为**异常长 meta 键**的兜底（正常数据永不触发），`title` 兜住可读性。
         *   · `reference` ⇒ `min-w-16`（**可增长**）：它的标签是 `图片3 · 404室-主机位角色站位`
         *     这类带名称的，截断会让用户看不出是哪张（门禁 L163 明确要求名称可见）；
         *     用 `max-w-[50%]` 兜住极端长名，正文靠 `min-w-0 flex-1` 让位，再配 `title` 悬停可读全名。
         */
        const widthCls =
          b.kind === "reference"
            ? "min-w-16 max-w-[50%] truncate"
            : "w-16 text-center truncate";
        return (
          <div
            key={i}
            data-block-kind={b.kind}
            className="flex gap-2 rounded-lg bg-white/[0.08] px-2 py-1.5"
          >
            {/* 左侧标签：定宽列，时间码对齐后时间轴一眼能扫 */}
            <span
              className={cn(
                "mt-px h-fit shrink-0 rounded px-1.5 py-0.5 leading-4",
                widthCls,
                st.chip,
                st.label
              )}
              title={label}
            >
              {label}
            </span>
            <RichText
              text={(b as { text: string }).text}
              className="min-w-0 flex-1"
            />
          </div>
        );
      })}
    </div>
  );
}